import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Input, Component } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '@studiobuki/web-core/lib/user';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { RoutingService } from 'src/app/services/routing.service';
import { filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, firstValueFrom, Subject } from 'rxjs';
import { TRegionNameEnglish } from '@shared/jp/shipping/interfaces';
import { REGION_NAME_ENGLISH_DEFAULT } from '@shared/jp/shipping/constants';
import type { IBookInOrder, IOrderData } from '@shared/jp/interfaces';
import type { TCouponData } from '@shared/common/interfaces';
import { LIMIT_BOOKS_TO_ORDER } from '@shared/common/constants';
import { ProductionService } from 'src/app/services/production.service';
import { LoaderService } from '@studiobuki/web-core/lib/loader';
import { DiscountService } from 'src/app/services/discount.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import type { IBookCover, IBookWrapping } from '@shared/jp/data/products';
import { Logger } from '@shared/common/logger';
import type { IShipping } from '@shared/jp/data/shipping';
import {
  FEToBEProduct,
  getCalculatorItems,
  getProductByBookData,
  getProductPrice,
} from 'src/utils';
import type { TBookCover, TProductAlias } from '@shared/jp/product';
import Subscriber from '@shared/common/subscriber';
import type { IProduct } from 'src/types';
import { OrderService } from '@studiobuki/web-core/lib/order';
import type { TBookData } from '@shared/jp/models';
import { filterNullable } from '@studiobuki/web-core';
import { EVendor } from '@shared/jp/enums';
import { DeleteProductDialogComponent } from '../delete-product-dialog/delete-product-dialog.component';

const log = new Logger('SectionCartComponent');

@Component({
  selector: 'app-section-cart',
  templateUrl: './section-cart.component.html',
  styleUrls: ['./section-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCartComponent implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  @Input() set region(value: TRegionNameEnglish) {
    this.region$.next(value);
  }

  public readonly region$ = new BehaviorSubject<TRegionNameEnglish>(
    REGION_NAME_ENGLISH_DEFAULT,
  );

  // public orderId$ = new ReplaySubject<IOrderData['orderId']>(1);
  public readonly order$ = new BehaviorSubject<IOrderData | undefined>(
    undefined,
  );

  public readonly products$ = this.userService.booksInCartList$.pipe(
    map((books) => {
      log.info('load: _userServiceBooksList map');

      return books.map((book) => getProductByBookData(book));
    }),
  );

  public readonly getDiscount$ = this.discountService.getDiscount$;

  public readonly isLimitExceeded$ = this.products$.pipe(
    map((products) => products.length > LIMIT_BOOKS_TO_ORDER),
  );

  public readonly isLoading$ = new BehaviorSubject(false);

  public readonly isProceed$ = combineLatest([
    this.products$,
    this.isLimitExceeded$,
    this.isLoading$,
  ]).pipe(
    map(
      ([products, isLimitExceeded, isLoading]) =>
        !isLoading && products.length && !isLimitExceeded,
    ),
  );

  public readonly shipping$ = this._productionService
    .getShippings$(
      combineLatest([this.products$, this.region$]).pipe(
        map(([products, region]) => ({
          booksCount: products.length || 1,
          regionName: region,
        })),
      ),
    )
    .pipe(
      map((shippings) => shippings[0]),
      filter((method): method is IShipping => !!method),
      shareReplay(1),
      takeUntil(this._destroy$),
    );

  public readonly booksInOrder$ = this.products$.pipe(
    map((products) => products.map(FEToBEProduct)),
  );

  /** updated by `<app-coupon>` */
  public readonly coupon$ = new BehaviorSubject<TCouponData | undefined>(
    undefined,
  );

  public readonly calculatorItems$ = combineLatest([
    this.products$,
    this.discountService.activeDiscountCampaign$,
    this.coupon$,
    this.shipping$,
  ]).pipe(
    switchMap(([products, discountCampaign, coupon, shipping]) =>
      getCalculatorItems(
        this.discountService.getDiscount$,
        this.discountService.getShippingDiscount$,
        products,
        coupon,
        shipping,
      ),
    ),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  public coverNameMap: { [key in TBookCover]: string } = {
    hard: 'Hard Cover',
    soft: 'Soft Cover',
  };

  public readonly LIMIT_BOOKS_TO_ORDER = LIMIT_BOOKS_TO_ORDER;

  private _sub = new Subscriber();

  constructor(
    public router: Router,
    public userService: UserService<TProductAlias, TBookData>,
    public discountService: DiscountService,
    private _orderService: OrderService,
    private _firebaseService: FirebaseService,
    private _dialog: MatDialog,
    private _routing: RoutingService,
    private _loaderService: LoaderService,
    private _productionService: ProductionService,
  ) {}

  public readonly getProductPrice = getProductPrice;

  async ngOnInit() {
    const order = await this._createOrder();
    this.order$.next(order);
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._sub.unsubscribe();
  }

  private async showRemovalConfirmationDialog(onConfirm: () => Promise<any>) {
    const confirmed = await firstValueFrom(
      this._dialog.open(DeleteProductDialogComponent).afterClosed(),
    );

    if (confirmed) {
      await onConfirm();
    }
  }

  public removeProduct(p: IProduct) {
    return this.showRemovalConfirmationDialog(() =>
      this._firebaseService.deleteUserBook(p.bookId),
    );
  }

  public async selectWrapping(p: IProduct, name: IBookWrapping['name']) {
    const wrapping = p.wrappings.find((w) => w.name === name);

    if (!wrapping) {
      log.error(
        'selectWrapping - wrapping not found by name',
        name,
        'in product',
        p,
      );
      return;
    }

    await this._firebaseService.setUserBook(
      p.bookId,
      { wrapping: wrapping.id },
      { merge: true },
    );
  }

  public async selectCover(p: IProduct, id: IBookCover['id']) {
    const cover = p.covers.find((c) => c.id === id);

    if (!cover) {
      log.error('selectCover - cover not found by id', id, 'in product', p);
      return;
    }

    await this._firebaseService.setUserBook(
      p.bookId,
      { cover: id },
      { merge: true },
    );
  }

  public addAnotherBookButtonClick() {
    // log.info('addAnotherBookButtonClick');
    return this._routing.goToIndex({ fragment: 'preform' });
  }

  /**
   * Navigates to order page. Make sure you do check by isProceed
   * before triggering this funciton.
   */
  async proceed() {
    this.isLoading$.next(true);

    const orderData = await firstValueFrom(this.order$.pipe(filterNullable()));
    const { orderId } = orderData;

    this._loaderService.show();

    await this._updateOrder(orderId);

    this.isLoading$.next(false);

    return this.router.navigate(['order', orderId]);
  }

  private async _getBooksInOrder() {
    const userBooks = await firstValueFrom(this.userService.booksInCartList$);
    const booksInOrder: IBookInOrder[] = userBooks.map((_) => ({
      alias: _.alias,
      bookId: _.bookId,
      status: 0,
      wrapping: _.wrapping || 'standart',
      cover: _.cover,
      count: _.count,
    }));

    return booksInOrder;
  }

  /**
   * Returns orderId and creates new order if we didn`t
   * have it before
   */
  private async _createOrder() {
    this.isLoading$.next(true);

    // do not write until initialized
    await firstValueFrom(this._firebaseService.inited$);

    const userOrders = await this._firebaseService.getUserOrders();

    const uncompletedOrder = userOrders.find(
      (_) =>
        _.status === 0 &&
        !_.addressBillingData &&
        !_.addressShippingData &&
        _.vendor === EVendor.tokyoMetro &&
        // ensure that order is not errored
        !_.error &&
        // ensure that we have the customer id
        _.customerId, // buki === 0 or undefined
    );

    let order: IOrderData;

    if (uncompletedOrder) {
      order = (await this._updateOrder(uncompletedOrder.orderId)) as IOrderData;
    } else {
      const booksInOrder = await this._getBooksInOrder();

      order = (await this._orderService.createOrder(
        booksInOrder,
      )) as IOrderData;
    }

    this.isLoading$.next(false);

    return order;
  }

  private async _updateOrder(orderId: IOrderData['orderId']) {
    const booksInOrder = await this._getBooksInOrder();

    return this._orderService.updateOrder(orderId, {
      books: booksInOrder,
    });
  }
}
